// databaseService.ts - Simple API-only version
import { Character } from "./types";

class DatabaseService {
	private apiUrl = "/api"; // Relative URL since it's served from same domain

	private async fetchApi(
		endpoint: string,
		options: RequestInit = {},
	): Promise<any> {
		const url = `${this.apiUrl}${endpoint}`;
		console.log(url);
		try {
			const response = await fetch(url, {
				headers: {
					"Content-Type": "application/json",
					...options.headers,
				},
				...options,
			});

			if (!response.ok) {
				const errorText = await response.text();
				let errorMessage;

				try {
					const errorData = JSON.parse(errorText);
					errorMessage = errorData.error || errorData.message;
				} catch {
					errorMessage = `Server error: ${response.status} ${response.statusText}`;
				}

				throw new Error(errorMessage);
			}

			// Handle empty responses
			const text = await response.text();

			console.log(url);

			return text ? JSON.parse(text) : {};
		} catch (error) {
			if (error instanceof TypeError && error.message.includes("fetch")) {
				throw new Error(
					"Unable to connect to server. Please check your connection.",
				);
			}
			throw error;
		}
	}

	async init(): Promise<void> {
		try {
			await this.fetchApi("/health");
			console.log("Database service connected successfully");
		} catch (error) {
			console.error("Database service connection failed:", error);
			throw new Error(
				`Failed to connect to database service: ${
					(error as Error).message
				}`,
			);
		}
	}

	async saveCharacter(character: Character): Promise<number> {
		const response = await this.fetchApi("/characters", {
			method: "POST",
			body: JSON.stringify(character),
		});
		return response.id;
	}

	async updateCharacter(id: number, character: Character): Promise<void> {
		await this.fetchApi(`/characters/${id}`, {
			method: "PUT",
			body: JSON.stringify(character),
		});
	}

	async getCharacter(id: number): Promise<Character | null> {
		try {
			return await this.fetchApi(`/characters/${id}`);
		} catch (error) {
			const errorMessage = (error as Error).message;
			if (
				errorMessage.includes("404") ||
				errorMessage.includes("not found")
			) {
				return null;
			}
			throw error;
		}
	}

	async getAllCharacters(): Promise<
		{
			id: number;
			name: string;
			level: number;
			class: string;
			race: string;
			updatedAt: string;
		}[]
	> {
		return await this.fetchApi("/characters");
	}

	async deleteCharacter(id: number): Promise<void> {
		await this.fetchApi(`/characters/${id}`, {
			method: "DELETE",
		});
	}

	// Future game data methods
	async getSpells(
		level?: number,
		characterClass?: string,
		school?: string,
	): Promise<any[]> {
		const params = new URLSearchParams();
		if (level !== undefined) params.append("level", level.toString());
		if (characterClass) params.append("class", characterClass);
		if (school) params.append("school", school);

		const query = params.toString() ? `?${params.toString()}` : "";
		return await this.fetchApi(`/spells${query}`);
	}

	async getEquipment(
		category?: string,
		rarity?: string,
		type?: string,
	): Promise<any[]> {
		const params = new URLSearchParams();
		if (category) params.append("category", category);
		if (rarity) params.append("rarity", rarity);
		if (type) params.append("type", type);

		const query = params.toString() ? `?${params.toString()}` : "";
		return await this.fetchApi(`/equipment${query}`);
	}

	// Health check
	async checkHealth(): Promise<boolean> {
		try {
			await this.fetchApi("/health");
			return true;
		} catch {
			return false;
		}
	}
}

// Export singleton instance
export const databaseService = new DatabaseService();
